/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 **/
import { useEffect, useRef, useCallback } from 'react'
import { clearSession } from '@az/auth-ping'
import {
  IDLE_TIMEOUT_MINUTES,
  ONE_SECOND,
  MINUTE_MS,
  BACKGROUND_CHECK_INTERVAL
} from '../constants'

export const useIdleTimeout = () => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const lastActivityRef = useRef<number>(Date.now())
  const idleTimeout = IDLE_TIMEOUT_MINUTES * ONE_SECOND * MINUTE_MS

  const forceLogoutCheck = useCallback(() => {
    const timeSinceLastActivity = Date.now() - lastActivityRef.current
    if (timeSinceLastActivity >= idleTimeout) {
      clearSession()
      return true
    }
    return false
  }, [idleTimeout])

  const resetTimeout = useCallback(() => {
    lastActivityRef.current = Date.now()

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    timeoutRef.current = setTimeout(() => {
      clearSession()
    }, idleTimeout)
  }, [idleTimeout])

  const clearIdleTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }, [])

  const handleVisibilityChange = useCallback(() => {
    const backgroundCheckInterval = BACKGROUND_CHECK_INTERVAL * ONE_SECOND
    if (document.hidden) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
      intervalRef.current = setInterval(() => {
        forceLogoutCheck()
      }, backgroundCheckInterval)
      return
    }

    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }

    if (!forceLogoutCheck()) {
      const timeSinceLastActivity = Date.now() - lastActivityRef.current
      const remainingTime = Math.max(0, idleTimeout - timeSinceLastActivity)

      if (remainingTime === 0) {
        clearSession()
      } else {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current)
        }
        timeoutRef.current = setTimeout(() => {
          clearSession()
        }, remainingTime)
      }
    }
  }, [idleTimeout, forceLogoutCheck])

  useEffect(() => {
    const desktopEvents = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'click',
      'keydown',
      'wheel'
    ] as const
    const touchEvents = [
      'touchstart',
      'touchmove',
      'touchend',
      'touchcancel',
      'pointerdown',
      'pointermove',
      'pointerup',
      'pointercancel'
    ] as const
    const otherEvents = [
      'orientationchange',
      'focus',
      'blur',
      'resize',
      'devicemotion',
      'deviceorientation'
    ] as const

    const resetTimeoutHandler = () => {
      resetTimeout()
    }

    const passiveOptions: AddEventListenerOptions = { passive: true, capture: true }
    const passiveWindowOptions: AddEventListenerOptions = { passive: true }

    resetTimeout()

    desktopEvents.forEach(event => {
      document.addEventListener(event, resetTimeoutHandler, true)
    })

    touchEvents.forEach(event => {
      try {
        document.addEventListener(event, resetTimeoutHandler, passiveOptions)
      } catch (e) {
        document.addEventListener(event, resetTimeoutHandler, true)
      }
    })

    otherEvents.forEach(event => {
      document.addEventListener(event, resetTimeoutHandler, true)
    })

    document.addEventListener('visibilitychange', handleVisibilityChange, true)

    const windowEvents = ['touchstart', 'touchmove', 'pointerdown', 'pointermove'] as const
    windowEvents.forEach(event => {
      try {
        window.addEventListener(event, resetTimeoutHandler, passiveWindowOptions)
      } catch (e) {
        window.addEventListener(event, resetTimeoutHandler, false)
      }
    })

    return () => {
      clearIdleTimeout()

      desktopEvents.forEach(event => {
        document.removeEventListener(event, resetTimeoutHandler, true)
      })

      touchEvents.forEach(event => {
        try {
          document.removeEventListener(event, resetTimeoutHandler, passiveOptions)
        } catch (e) {
          document.removeEventListener(event, resetTimeoutHandler, true)
        }
      })

      otherEvents.forEach(event => {
        document.removeEventListener(event, resetTimeoutHandler, true)
      })

      document.removeEventListener('visibilitychange', handleVisibilityChange, true)

      windowEvents.forEach(event => {
        try {
          window.removeEventListener(event, resetTimeoutHandler, passiveWindowOptions)
        } catch (e) {
          window.removeEventListener(event, resetTimeoutHandler, false)
        }
      })
    }
  }, [resetTimeout, clearIdleTimeout, handleVisibilityChange, idleTimeout])

  return { resetTimeout, clearIdleTimeout }
}
